// @ts-ignore
import Drawing from 'dxf-writer';
import { 
  PlacedBlock, 
  SocketConnection, 
  PlanSheet, 
  IsoPoint,
  BlockSystem
} from '@/app/types/cad';
import { isoToScreen } from '@/app/utils/isometric';
import { transformPoint } from '@/app/utils/transform';

// --- CONSTANTS ---
const PIXELS_PER_MM = 3.7795275591;

// ACI Colors (AutoCAD Color Index)
const ACI = {
  LAYER: 256, 
  RED: 1,
  YELLOW: 2,
  GREEN: 3,
  CYAN: 4,
  BLUE: 5,
  MAGENTA: 6,
  WHITE: 7,
  GRAY: 8,
  LIGHT_GRAY: 9
};

// --- HELPER FUNCTIONS ---

const isValid = (n: any): boolean => {
  return typeof n === 'number' && !isNaN(n) && isFinite(n);
};

const sanitizeLayerName = (name: string): string => {
  if (!name) return "DEFAULT";
  return name.replace(/[^a-zA-Z0-9_-]/g, '_').toUpperCase().substring(0, 30);
};

// --- EXPORT FUNCTION ---

interface ExportDxfOptions {
  plan: PlanSheet;
  blocks: PlacedBlock[];
  connections: SocketConnection[];
  systems?: BlockSystem[];
  gridSize?: number;
  projectName?: string;
  // Legacy/Unused options
  textAnnotations?: any[];
  placedLabels?: any[];
  labelTemplates?: any[];
  titleBlockTemplate?: any;
  titleBlockData?: any;
  titleBlockPosition?: any;
  pipeTypes?: any[];
  showDebugGrid?: boolean;
}

export function exportPlanToDxf(options: ExportDxfOptions) {
  try {
    const { 
      plan, 
      blocks = [], 
      systems = [], 
      gridSize = 20,
      projectName
    } = options;

    if (!plan) {
      console.error("DXF Export: Plan is missing");
      return;
    }

    // Initialize dxf-writer
    // @ts-ignore
    const d = new Drawing();
    
    // Units: Millimeters (4)
    d.setUnits('Millimeters'); 

    // Dimensions
    const pWidthMm = isValid(plan.width) ? plan.width : 420;
    const pHeightMm = isValid(plan.height) ? plan.height : 297;
    
    const paperWidthPx = pWidthMm * PIXELS_PER_MM;
    const paperHeightPx = pHeightMm * PIXELS_PER_MM;

    const vpScale = (plan.viewport && isValid(plan.viewport.scale)) ? plan.viewport.scale : 1.0;
    const vpCenterX = (plan.viewport && isValid(plan.viewport.centerX)) ? plan.viewport.centerX : 0;
    const vpCenterY = (plan.viewport && isValid(plan.viewport.centerY)) ? plan.viewport.centerY : 0;

    const paperCenterX = paperWidthPx / 2;
    const paperCenterY = paperHeightPx / 2;

    // --- Transforms ---

    const isoToDxf = (isoPos: IsoPoint) => {
        if (!isoPos || !isValid(isoPos.u) || !isValid(isoPos.v)) return { x: 0, y: 0, valid: false };
        
        const worldPos = isoToScreen(isoPos, gridSize);
        const dx = (worldPos.x - vpCenterX) * vpScale;
        const dy = (worldPos.y - vpCenterY) * vpScale;
        
        const pxX = paperCenterX + dx;
        const pxY = paperCenterY + dy;
        
        // Convert to MM and Flip Y
        const xMm = pxX / PIXELS_PER_MM;
        const yMm = (paperHeightPx - pxY) / PIXELS_PER_MM;
        
        return { x: xMm, y: yMm, valid: true };
    };

    // --- Layer Management ---
    
    const definedLayers = new Set<string>();

    const safeAddLayer = (name: string, color: number, ltype: string) => {
      const safeName = sanitizeLayerName(name);
      if (definedLayers.has(safeName)) return safeName;
      
      if (safeName !== '0') {
        try {
          d.addLayer(safeName, color, ltype);
        } catch (e) {
          console.warn("Could not add layer", safeName, e);
        }
      }
      definedLayers.add(safeName);
      return safeName;
    };

    // Define Base Layers
    safeAddLayer('BLOCKS', ACI.WHITE, 'CONTINUOUS');
    safeAddLayer('HIDDEN', ACI.GRAY, 'DASHED');

    // Define System Layers
    systems.forEach(sys => {
        let color = ACI.WHITE;
        if (sys.color?.includes('red')) color = ACI.RED;
        else if (sys.color?.includes('blue')) color = ACI.BLUE;
        else if (sys.color?.includes('green')) color = ACI.GREEN;
        else if (sys.color?.includes('cyan')) color = ACI.CYAN;
        else if (sys.color?.includes('magenta')) color = ACI.MAGENTA;
        else if (sys.color?.includes('yellow')) color = ACI.YELLOW;
        
        safeAddLayer(sys.name, color, 'CONTINUOUS');
    });

    // --- Helpers ---
    
    const safeDrawLine = (x1: number, y1: number, x2: number, y2: number, layer: string) => {
        if (!isValid(x1) || !isValid(y1) || !isValid(x2) || !isValid(y2)) return;
        const safeL = definedLayers.has(sanitizeLayerName(layer)) ? sanitizeLayerName(layer) : '0';
        d.drawLine(x1, y1, x2, y2, safeL);
    };

    const safeDrawCircle = (x: number, y: number, r: number, layer: string) => {
       if (!isValid(x) || !isValid(y) || !isValid(r) || r <= 0) return;
       const safeL = definedLayers.has(sanitizeLayerName(layer)) ? sanitizeLayerName(layer) : '0';
       d.drawCircle(x, y, r, safeL);
    };

    // --- Drawing ---

    blocks.forEach(block => {
        if (!block.blockData?.elements) return;

        let layerName = 'BLOCKS';
        const sys = systems.find(s => s.id === block.systemId);
        if (sys) layerName = sys.name;

        block.blockData.elements.forEach(el => {
            if (el.type === 'line') {
                const s = transformPoint(el.start, block.transform);
                const e = transformPoint(el.end, block.transform);
                const p1 = isoToDxf(s);
                const p2 = isoToDxf(e);
                
                if (p1.valid && p2.valid) {
                    const l = (el.lineStyle === 'dashed') ? 'HIDDEN' : layerName;
                    safeDrawLine(p1.x, p1.y, p2.x, p2.y, l);
                }
            } else if (el.type === 'circle') {
                const c = transformPoint(el.center, block.transform);
                const p = isoToDxf(c);
                if (p.valid) {
                   const rPx = (el.radius * gridSize) * vpScale;
                   const rMm = rPx / PIXELS_PER_MM;
                   safeDrawCircle(p.x, p.y, rMm, layerName);
                }
            }
            // Dimensions ignored (as requested)
            // Text ignored
            // Connections ignored
        });
    });

    // --- EXPORT ---
    let content = d.toDxfString();
    
    // Fix Line Endings
    content = content.replace(/\r?\n/g, '\r\n');

    const blob = new Blob([content], { type: 'application/dxf' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${projectName || 'plan'}_final.dxf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => URL.revokeObjectURL(url), 1000);

  } catch (err) {
    console.error("DXF Export Error:", err);
    alert("DXF Export Failed: " + String(err));
  }
}
